import { useEffect, useState } from "react";
import { Briefcase, Plus, Users, Building2, Edit2, X, Check, Loader2, UserPlus, Building, Trash2, TrendingUp, HandHelping, ChevronDown, ChevronUp, Sparkles, KeyRound } from "lucide-react";
import HelpTip from "@/components/help-tip";

const BASE = import.meta.env.BASE_URL;

function authHeaders() {
  const token = localStorage.getItem("admin_token") || "";
  return { "Content-Type": "application/json", "x-admin-token": token };
}

export default function AdminBankers() {
  const [bankers, setBankers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [allocating, setAllocating] = useState<any | null>(null);

  const load = async () => {
    try {
      const r = await fetch(`${BASE}api/admin/bankers`, { headers: authHeaders() });
      if (r.ok) setBankers(await r.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-5 -mx-2 md:mx-0">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2"><Briefcase className="w-7 h-7 text-[#FF5C00]" /> Bankers</h1>
          <p className="text-white/50 text-sm mt-1">Manage the Favcy Advisors investment banking team — who they are, which investors they cover, which companies.</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add banker
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white/[0.04] rounded-lg animate-pulse" />)}</div>
      ) : bankers.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-12 text-center">
          <p className="text-white/50 text-sm">No bankers yet. Add your first one to start routing investor tasks.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bankers.map(b => (
            <BankerCard key={b.id} banker={b} onEdit={() => setEditing(b)} onAllocate={() => setAllocating(b)} onUpdate={load} />
          ))}
        </div>
      )}

      <ReallocationPanel />

      {createOpen && <BankerFormModal onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />}
      {editing && <BankerFormModal banker={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {allocating && <AllocationModal banker={allocating} onClose={() => setAllocating(null)} />}
    </div>
  );
}

function ReallocationPanel() {
  const [tab, setTab] = useState<"pending" | "approved" | "rejected">("pending");
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [decidingId, setDecidingId] = useState<number | null>(null);
  const [noteFor, setNoteFor] = useState<{ id: number; decision: "approved" | "rejected" } | null>(null);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}api/admin/reallocation-requests?status=${tab}`, { headers: authHeaders() });
      if (r.ok) setItems(await r.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (id: number, decision: "approved" | "rejected", decisionNote?: string) => {
    setDecidingId(id);
    try {
      await fetch(`${BASE}api/admin/reallocation-requests/${id}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ decision, decisionNote: decisionNote || null }),
      });
      await load();
    } catch {}
    setDecidingId(null);
    setNoteFor(null); setNote("");
  };

  return (
    <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl">
      <button onClick={() => setOpen(!open)} className="w-full px-5 py-4 flex items-center justify-between text-left">
        <div className="flex items-center gap-2">
          <HandHelping className="w-5 h-5 text-amber-400" />
          <h2 className="text-base font-bold text-white">Coverage reallocation requests</h2>
          {items.filter(i => i.status === "pending").length > 0 && tab === "pending" && (
            <span className="bg-amber-500 text-black text-[10px] font-bold rounded-full min-w-[20px] h-[20px] inline-flex items-center justify-center px-1.5">
              {items.length}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3">
          <div className="flex items-center gap-1 text-xs">
            {(["pending", "approved", "rejected"] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-3 py-1.5 rounded-full border ${tab === t ? "bg-amber-500/20 border-amber-500/40 text-amber-300" : "bg-white/[0.04] border-white/[0.1] text-white/60 hover:border-white/[0.2]"}`}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          {loading ? <p className="text-white/40 text-sm">Loading...</p>
            : items.length === 0 ? <p className="text-white/40 text-sm py-4">No {tab} requests.</p>
            : <div className="space-y-2">
              {items.map(r => {
                const isHandoff = r.kind === "hand_off";
                return (
                  <div key={r.id} className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[260px]">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${isHandoff ? "bg-red-500/15 text-red-300 border-red-500/30" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"}`}>
                            {isHandoff ? "Hand off" : "Request to cover"}
                          </span>
                          <p className="text-white text-sm font-medium">
                            <span className="text-white/60">{r.requester?.name || `Banker #${r.requestedBy}`}</span>
                            {" "}{isHandoff ? "wants to drop" : "wants to cover"}{" "}
                            <span className="text-white">{r.company?.name || `Portco #${r.companyId}`}</span>
                          </p>
                        </div>
                        {r.reason && <p className="text-white/65 text-xs mt-1.5 leading-relaxed italic border-l-2 border-white/15 pl-2">"{r.reason}"</p>}
                        <p className="text-white/40 text-[10px] mt-1.5 font-mono">
                          {new Date(r.createdAt).toLocaleString()}
                          {r.currentCoverers?.length > 0 && (
                            <> · current coverers: {r.currentCoverers.map((c: any) => c.name).join(", ")}</>
                          )}
                        </p>
                        {r.decisionNote && (
                          <p className="text-white/55 text-xs mt-2 bg-white/[0.04] border border-white/[0.08] rounded p-2">
                            <span className="text-white/40">Admin note:</span> {r.decisionNote}
                          </p>
                        )}
                      </div>
                      {r.status === "pending" && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => { setNoteFor({ id: r.id, decision: "approved" }); setNote(""); }} disabled={decidingId === r.id}
                            className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 disabled:opacity-50">
                            <Check className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button onClick={() => { setNoteFor({ id: r.id, decision: "rejected" }); setNote(""); }} disabled={decidingId === r.id}
                            className="bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/40 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 disabled:opacity-50">
                            <X className="w-3.5 h-3.5" /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>}
        </div>
      )}

      {noteFor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/[0.1] rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-white">{noteFor.decision === "approved" ? "Approve request" : "Reject request"}</h3>
              <button onClick={() => setNoteFor(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-white/55 text-xs mb-3">Add a note for the requester (optional). They'll see this in their inbox.</p>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder={noteFor.decision === "approved" ? "e.g. Approved — please coordinate with the existing coverer." : "e.g. Hold off this cohort — we'll revisit next month."}
              className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 resize-none" />
            <div className="flex gap-2 mt-4">
              <button onClick={() => decide(noteFor.id, noteFor.decision, note)}
                className={`flex-1 ${noteFor.decision === "approved" ? "bg-emerald-500 hover:bg-emerald-600 text-black" : "bg-red-500 hover:bg-red-600 text-white"} py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2`}>
                {noteFor.decision === "approved" ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />} Confirm {noteFor.decision}
              </button>
              <button onClick={() => setNoteFor(null)} className="px-4 py-2.5 border border-white/15 rounded-lg text-sm text-white/70">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BankerCard({ banker, onEdit, onAllocate, onUpdate }: any) {
  const [perf, setPerf] = useState<any>(null);
  useEffect(() => {
    fetch(`${BASE}api/admin/bankers/${banker.id}/performance`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null).then(setPerf).catch(() => {});
  }, [banker.id]);

  const toggleStatus = async () => {
    const newStatus = banker.status === "active" ? "inactive" : "active";
    await fetch(`${BASE}api/admin/bankers/${banker.id}`, {
      method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: newStatus }),
    });
    onUpdate();
  };

  const [origBusy, setOrigBusy] = useState(false);
  const toggleOriginationPermission = async () => {
    const next = !banker.canOriginateDeals;
    if (!next && !confirm(`Revoke origination permission for ${banker.name}? They will no longer be able to draft new curated deals (existing live deals are unaffected).`)) return;
    setOrigBusy(true);
    try {
      await fetch(`${BASE}api/admin/bankers/${banker.id}/origination-permission`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ enabled: next }),
      });
      onUpdate();
    } finally { setOrigBusy(false); }
  };

  const [resetBusy, setResetBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const sendResetLink = async () => {
    if (!confirm(`Send a fresh password-reset link to ${banker.name} (${banker.email})? This will invalidate any previous reset link and clear their "must change password" gate, so the password they set via the new link becomes their canonical one. Link is valid for 24 hours.`)) return;
    setResetBusy(true);
    setResetMsg(null);
    try {
      const r = await fetch(`${BASE}api/admin/bankers/${banker.id}/send-reset`, {
        method: "POST", headers: authHeaders(),
      });
      if (r.ok) {
        setResetMsg({ kind: "ok", text: `Reset link sent to ${banker.email}. They have 24 hours to use it.` });
      } else {
        const d = await r.json().catch(() => ({}));
        setResetMsg({ kind: "err", text: d?.error || "Could not send the reset link. Check server logs." });
      }
    } catch {
      setResetMsg({ kind: "err", text: "Network error. Try again in a moment." });
    } finally {
      setResetBusy(false);
      setTimeout(() => setResetMsg(null), 8000);
    }
  };

  const isLead = banker.role === "lead";
  const isActive = banker.status === "active";
  const canOriginate = !!banker.canOriginateDeals;

  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 hover:bg-white/[0.05] transition-colors">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-11 h-11 rounded-lg bg-[#FF5C00]/15 border border-[#FF5C00]/25 flex items-center justify-center flex-shrink-0">
            <Briefcase className="w-5 h-5 text-[#FF5C00]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-white text-base truncate">{banker.name}</p>
              {isLead && <span className="text-[9px] bg-[#FF5C00]/15 text-[#FF5C00] border border-[#FF5C00]/30 px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">Lead</span>}
              <span className={`text-[9px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                isActive ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-red-500/15 text-red-400 border border-red-500/30"
              }`}>{isActive ? "Active" : "Inactive"}</span>
              {canOriginate && (
                <span className="inline-flex items-center gap-1 text-[9px] bg-purple-500/15 text-purple-300 border border-purple-500/30 px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">
                  <Sparkles className="w-2.5 h-2.5" /> Can originate
                </span>
              )}
            </div>
            <p className="text-white/60 text-xs mt-1">{banker.title || "Investment Banker"}</p>
            <p className="text-white/40 text-xs mt-0.5">{banker.email}{banker.phone ? ` · ${banker.phone}` : ""}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          <button onClick={onAllocate} className="bg-[#FF5C00]/10 hover:bg-[#FF5C00]/20 border border-[#FF5C00]/30 text-[#FF5C00] px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Manage allocation
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleOriginationPermission}
              disabled={origBusy}
              title={canOriginate ? "Revoke origination permission" : "Grant origination permission"}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border transition-colors disabled:opacity-50 ${
                canOriginate
                  ? "bg-purple-500/15 hover:bg-purple-500/25 border-purple-500/30 text-purple-200"
                  : "bg-white/[0.06] hover:bg-white/[0.1] border-white/[0.1] text-white/70 hover:text-white"
              }`}
            >
              {origBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {canOriginate ? "Revoke origination" : "Grant origination"}
            </button>
            <HelpTip
              title="Origination permission"
              content={<>Lets this advisor draft and submit curated deals from <b>My originations</b>. Without it, the page is read-only and the "New origination" button stays disabled. Grant after they've completed onboarding and you trust them to source deals on the platform. Revoking does not pull down their existing live deals — only blocks new drafts.</>}
            />
          </div>
          <button
            onClick={sendResetLink}
            disabled={resetBusy || !isActive}
            title={isActive ? "Email a fresh password-reset link (24h)" : "Reactivate the advisor before sending a reset"}
            className="bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white/80 hover:text-white px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
          >
            {resetBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
            {resetBusy ? "Sending…" : "Send reset link"}
          </button>
          <button onClick={onEdit} title="Edit banker" className="bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white/80 hover:text-white px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
            <Edit2 className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={toggleStatus} title={isActive ? "Deactivate" : "Activate"} className="bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white/80 hover:text-white p-1.5 rounded-lg">
            {isActive ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {resetMsg && (
        <div className={`mt-3 text-xs px-3 py-2 rounded-md border ${resetMsg.kind === "ok" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-red-500/10 border-red-500/30 text-red-200"}`}>
          {resetMsg.text}
        </div>
      )}

      {banker.bio && <p className="text-white/50 text-xs mt-3 leading-relaxed">{banker.bio}</p>}

      {perf && (
        <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/[0.06]">
          <div className="bg-white/[0.03] rounded-lg px-3 py-2">
            <p className="text-[9px] text-white/40 uppercase tracking-wider">Tasks</p>
            <p className="text-xl font-bold font-mono text-white mt-0.5">{perf.total}</p>
          </div>
          <div className="bg-white/[0.03] rounded-lg px-3 py-2">
            <p className="text-[9px] text-white/40 uppercase tracking-wider">Active</p>
            <p className="text-xl font-bold font-mono text-amber-400 mt-0.5">{perf.inProgress}</p>
          </div>
          <div className="bg-white/[0.03] rounded-lg px-3 py-2">
            <p className="text-[9px] text-white/40 uppercase tracking-wider">Closed</p>
            <p className="text-xl font-bold font-mono text-emerald-400 mt-0.5">{perf.closed}</p>
          </div>
          <div className="bg-white/[0.03] rounded-lg px-3 py-2">
            <p className="text-[9px] text-white/40 uppercase tracking-wider">Artifacts</p>
            <p className="text-xl font-bold font-mono text-[#FF5C00] mt-0.5">{perf.artifacts}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function BankerFormModal({ banker, onClose, onSaved }: any) {
  const [form, setForm] = useState({
    name: banker?.name || "",
    email: banker?.email || "",
    phone: banker?.phone || "",
    title: banker?.title || "Investment Banker",
    bio: banker?.bio || "",
    avatarUrl: banker?.avatarUrl || "",
    password: "",
    role: banker?.role || "banker",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    // Identity-transfer safety. Email IS the login id for an advisor — once
    // you change it, the old address can no longer sign in and the new one
    // becomes the canonical account. Confirm before letting that happen.
    if (banker && form.email && form.email.toLowerCase().trim() !== (banker.email || "").toLowerCase().trim()) {
      const ok = window.confirm(
        `Change login email from "${banker.email}" to "${form.email.trim()}"?\n\n` +
        `This is how you transfer the dashboard to a new owner. The old address will stop working immediately. ` +
        `After saving, ask the new owner to use "Forgot password" (or click "Send reset link" on their row) to set their own password.`,
      );
      if (!ok) return;
    }
    setSaving(true); setError(null);
    try {
      const url = banker ? `${BASE}api/admin/bankers/${banker.id}` : `${BASE}api/admin/bankers`;
      const method = banker ? "PATCH" : "POST";
      const body: any = { ...form };
      if (banker && !form.password) delete body.password;
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      if (r.ok) onSaved();
      else { const e = await r.json(); setError(e.error || "Save failed"); }
    } catch { setError("Network error"); }
    setSaving(false);
  };

  const avatarLooksLikeUrl = /^https?:\/\/\S+$/i.test(form.avatarUrl.trim());

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/[0.1] rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">{banker ? "Edit banker" : "Add banker"}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {error && <div className="bg-red-500/10 border border-red-500/20 rounded p-2 text-red-400 text-xs mb-3">{error}</div>}
        <div className="space-y-3 text-sm">
          <div><label className="text-white/60 text-xs">Full name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Pranav Chaturvedi" className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50" /></div>
          <div><label className="text-white/60 text-xs">Email {banker && <span className="text-white/30">(login id — change to transfer dashboard to a new owner)</span>}</label>
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@favcy.in" className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-white/60 text-xs">Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="9899297767" className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50" /></div>
            <div><label className="text-white/60 text-xs">Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Investment Banker" className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50" /></div>
          </div>
          <div><label className="text-white/60 text-xs">Bio / expertise <span className="text-white/30">(shown to founder on Your Advisor card)</span></label>
            <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} placeholder="Brief bio or coverage expertise" className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50 resize-none" /></div>
          <div><label className="text-white/60 text-xs">Avatar URL <span className="text-white/30">(public image link; used on founder card)</span></label>
            <input value={form.avatarUrl} onChange={(e) => setForm({ ...form, avatarUrl: e.target.value })} placeholder="https://…/avatar.jpg" className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50" />
            {form.avatarUrl.trim() && !avatarLooksLikeUrl && (
              <p className="text-amber-400 text-xs mt-1.5">This needs to be an image URL starting with http:// or https:// — not an email or a path. The founder card will show initials instead of a broken image.</p>
            )}
            {avatarLooksLikeUrl && <img src={form.avatarUrl.trim()} alt="" className="w-12 h-12 rounded-full mt-2 object-cover border border-white/15" onError={(e) => (e.currentTarget.style.display = "none")} />}
          </div>
          <div><label className="text-white/60 text-xs">{banker ? "New password (leave blank to keep)" : "Password (min 6 chars)"}</label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50" /></div>
          <div><label className="text-white/60 text-xs">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white focus:outline-none focus:border-[#FF5C00]/50">
              <option value="banker" className="bg-[#111] text-white">Banker</option>
              <option value="lead" className="bg-[#111] text-white">Lead Banker</option>
            </select></div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={saving} className="flex-1 bg-[#FF5C00] hover:bg-[#E55200] text-white py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : "Save"}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 border border-white/15 rounded-lg text-sm text-white/70 hover:text-white">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AllocationModal({ banker, onClose }: { banker: any; onClose: () => void }) {
  const [assignments, setAssignments] = useState<any>({ investors: [], companies: [] });
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [a, c] = await Promise.all([
        fetch(`${BASE}api/admin/bankers/${banker.id}/assignments`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BASE}api/companies`, { headers: authHeaders() }).then(r => r.ok ? r.json() : []),
      ]);
      setAssignments(a); setAllCompanies(c);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [banker.id]);

  const assignCompany = async (companyId: number) => {
    await fetch(`${BASE}api/admin/bankers/${banker.id}/assign-company`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ companyId, isPrimary: true }),
    });
    load();
  };
  const unassignCompany = async (companyId: number) => {
    await fetch(`${BASE}api/admin/bankers/${banker.id}/assign-company/${companyId}`, { method: "DELETE", headers: authHeaders() });
    load();
  };

  const assignedCoIds = new Set(assignments.companies.map((a: any) => a.companyId));

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/[0.1] rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2"><Building2 className="w-5 h-5 text-[#FF5C00]" /> Portfolio coverage — {banker.name}</h2>
            <p className="text-white/50 text-xs mt-1">Assign the portfolio companies this banker owns. Investors reach them automatically whenever they ask about a covered company.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center justify-between mb-3 px-1">
          <p className="text-xs text-white/40 uppercase tracking-wider font-semibold">All companies</p>
          <p className="text-xs text-[#FF5C00] font-mono">{assignments.companies.length} assigned</p>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? <p className="text-white/40 text-sm">Loading...</p> : (
            allCompanies.length === 0 ? <p className="text-white/40 text-sm text-center py-6">No companies in system.</p> :
            allCompanies.map((co: any) => {
              const assigned = assignedCoIds.has(co.id);
              return (
                <div key={co.id} className={`flex items-center justify-between p-3 rounded-lg border ${assigned ? "bg-[#FF5C00]/5 border-[#FF5C00]/20" : "bg-white/[0.03] border-white/[0.06]"}`}>
                  <div>
                    <p className="font-medium text-sm text-white">{co.name}</p>
                    {co.tagline && <p className="text-white/50 text-xs line-clamp-1">{co.tagline}</p>}
                  </div>
                  {assigned ? (
                    <button onClick={() => unassignCompany(co.id)} className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1 px-2 py-1"><Trash2 className="w-3 h-3" /> Remove</button>
                  ) : (
                    <button onClick={() => assignCompany(co.id)} className="bg-[#FF5C00] hover:bg-[#E55200] text-white text-xs px-3 py-1.5 rounded flex items-center gap-1"><Building className="w-3 h-3" /> Assign</button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
